// src/utils/vehicleAssignments.ts — Vehicle / Plant → Employee assignment registry
//
// Business rule (per PM/PC requirement):
//   - A vehicle, truck, or piece of plant is assigned to an EMPLOYEE (not a job).
//   - The assignment remains in effect until either:
//       (a) it is explicitly unassigned (with a confirmation safeguard), or
//       (b) the employee is assigned a different vehicle/truck/plant — which
//           automatically supersedes (closes out) their previous assignment.
//   - Every assign / reassign / unassign action is written to a permanent,
//     timestamped history log — kept for future reference and exportable as
//     a weekly file (see vehicleAssignmentReports.ts).
//
// Persistence: localStorage (same pattern as utils/archiveRegistry.ts), so
// assignments and their history survive page refreshes rather than living
// only in module memory.

export type AssignableCategory = 'VEHICLE' | 'PLANT'

export type AssignmentAction = 'ASSIGN' | 'REASSIGN' | 'UNASSIGN'

export interface CurrentAssignment {
  assetId: string
  assetName: string
  assetNumber: string
  category: AssignableCategory
  employeeId: string
  employeeName: string
  effectiveDate: string   // yyyy-mm-dd
  reason: string
  recordedByName: string
  recordedAt: string      // ISO timestamp
}

export interface AssignmentHistoryEntry {
  id: string
  action: AssignmentAction
  assetId: string
  assetName: string
  assetNumber: string
  category: AssignableCategory
  employeeId: string | null
  employeeName: string | null
  // populated when action === 'REASSIGN' or 'UNASSIGN' to show what/who it replaced or removed
  previousEmployeeId: string | null
  previousEmployeeName: string | null
  effectiveDate: string
  reason: string
  recordedByName: string
  recordedAt: string
}

const CURRENT_KEY = 'tp_vehicle_assignments_current'
const HISTORY_KEY = 'tp_vehicle_assignment_history'

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

// Seed data so the register isn't empty on first load (demo continuity).
// Only used if nothing has been persisted yet.
const SEED_CURRENT: CurrentAssignment[] = [
  { assetId: 'eq1', assetName: 'Ford Ranger XL 2023', assetNumber: 'VEH-001', category: 'VEHICLE',
    employeeId: 'e1', employeeName: 'Tony Rivera', effectiveDate: '2026-06-01',
    reason: 'Assigned as primary vehicle for Kessels Rd job crew', recordedByName: 'Sarah Lee', recordedAt: '2026-06-01T08:00:00Z' },
  { assetId: 'eq2', assetName: 'Toyota Hilux SR 2022', assetNumber: 'VEH-002', category: 'VEHICLE',
    employeeId: 'e7', employeeName: 'Mike Torres', effectiveDate: '2026-06-01',
    reason: 'Assigned Pacific Mwy — long-distance crew vehicle', recordedByName: 'Mike Walsh', recordedAt: '2026-06-01T08:05:00Z' },
  { assetId: 'eq5', assetName: 'Isuzu NPS 2021', assetNumber: 'VEH-003', category: 'VEHICLE',
    employeeId: 'e3', employeeName: 'Sam Nguyen', effectiveDate: '2026-06-01',
    reason: 'Assigned Energex Annerley crew vehicle', recordedByName: 'Mike Walsh', recordedAt: '2026-06-01T08:10:00Z' },
]

const SEED_HISTORY: AssignmentHistoryEntry[] = SEED_CURRENT.map((a, i) => ({
  id: `seed_${i}`,
  action: 'ASSIGN',
  assetId: a.assetId, assetName: a.assetName, assetNumber: a.assetNumber, category: a.category,
  employeeId: a.employeeId, employeeName: a.employeeName,
  previousEmployeeId: null, previousEmployeeName: null,
  effectiveDate: a.effectiveDate, reason: a.reason,
  recordedByName: a.recordedByName, recordedAt: a.recordedAt,
}))

function readJson<T>(key: string, fallback: T): T {
  if (!canUseStorage()) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as T
    return parsed
  } catch {
    return fallback
  }
}

function writeJson<T>(key: string, value: T) {
  if (!canUseStorage()) return
  window.localStorage.setItem(key, JSON.stringify(value))
}

let seeded = false
function ensureSeeded() {
  if (seeded || !canUseStorage()) return
  seeded = true
  if (window.localStorage.getItem(CURRENT_KEY) == null) writeJson(CURRENT_KEY, SEED_CURRENT)
  if (window.localStorage.getItem(HISTORY_KEY) == null) writeJson(HISTORY_KEY, SEED_HISTORY)
}

export function getCurrentAssignments(): CurrentAssignment[] {
  ensureSeeded()
  return readJson<CurrentAssignment[]>(CURRENT_KEY, SEED_CURRENT)
}

export function getAssignmentHistory(): AssignmentHistoryEntry[] {
  ensureSeeded()
  return readJson<AssignmentHistoryEntry[]>(HISTORY_KEY, SEED_HISTORY)
    .slice()
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))
}

export function getAssignmentForAsset(assetId: string): CurrentAssignment | null {
  return getCurrentAssignments().find(a => a.assetId === assetId) ?? null
}

export function getAssignmentForEmployee(employeeId: string): CurrentAssignment | null {
  return getCurrentAssignments().find(a => a.employeeId === employeeId) ?? null
}

export function getHistoryForAsset(assetId: string): AssignmentHistoryEntry[] {
  return getAssignmentHistory().filter(h => h.assetId === assetId)
}

function saveAll(current: CurrentAssignment[], history: AssignmentHistoryEntry[]) {
  writeJson(CURRENT_KEY, current)
  writeJson(HISTORY_KEY, history)
}

/**
 * Assign (or reassign) an asset to an employee.
 * - If the asset was already assigned to someone else, that assignment is closed out.
 * - If the employee already had a different asset assigned, that previous
 *   assignment is automatically superseded (per business rule).
 */
export function assignAssetToEmployee(
  asset: { id: string; name: string; assetNumber: string; category: AssignableCategory },
  employeeId: string,
  employeeName: string,
  reason: string,
  recordedByName: string,
) {
  const current = getCurrentAssignments()
  const history = getAssignmentHistory()
  const now = new Date()
  const nowIso = now.toISOString()
  const today = nowIso.slice(0, 10)

  const priorHolderOfAsset = current.find(a => a.assetId === asset.id) ?? null
  const priorAssetOfEmployee = current.find(a => a.employeeId === employeeId && a.assetId !== asset.id) ?? null

  const action: AssignmentAction = priorHolderOfAsset ? 'REASSIGN' : 'ASSIGN'

  // Remove any existing current-assignment row for this asset, and any
  // existing row where this employee held a *different* asset.
  let nextCurrent = current.filter(a => a.assetId !== asset.id && a.employeeId !== employeeId)

  const newEntry: CurrentAssignment = {
    assetId: asset.id, assetName: asset.name, assetNumber: asset.assetNumber, category: asset.category,
    employeeId, employeeName, effectiveDate: today, reason, recordedByName, recordedAt: nowIso,
  }
  nextCurrent = [newEntry, ...nextCurrent]

  const newHistory: AssignmentHistoryEntry[] = [...history]
  newHistory.unshift({
    id: `ha_${now.getTime()}`,
    action,
    assetId: asset.id, assetName: asset.name, assetNumber: asset.assetNumber, category: asset.category,
    employeeId, employeeName,
    previousEmployeeId: priorHolderOfAsset?.employeeId ?? null,
    previousEmployeeName: priorHolderOfAsset?.employeeName ?? null,
    effectiveDate: today, reason, recordedByName, recordedAt: nowIso,
  })

  // If this employee had a different asset, log that it was automatically superseded.
  if (priorAssetOfEmployee) {
    newHistory.unshift({
      id: `ha_${now.getTime()}_superseded`,
      action: 'UNASSIGN',
      assetId: priorAssetOfEmployee.assetId, assetName: priorAssetOfEmployee.assetName,
      assetNumber: priorAssetOfEmployee.assetNumber, category: priorAssetOfEmployee.category,
      employeeId: null, employeeName: null,
      previousEmployeeId: priorAssetOfEmployee.employeeId, previousEmployeeName: priorAssetOfEmployee.employeeName,
      effectiveDate: today,
      reason: `Automatically superseded — ${employeeName} was assigned ${asset.assetNumber} (${asset.name})`,
      recordedByName, recordedAt: nowIso,
    })
  }

  saveAll(nextCurrent, newHistory)
  return { action, supersededAsset: priorAssetOfEmployee }
}

/**
 * Unassign an asset from whoever currently holds it.
 * Callers should gate this behind a confirmation step in the UI —
 * this function performs the action once confirmed.
 */
export function unassignAsset(
  assetId: string,
  reason: string,
  recordedByName: string,
) {
  const current = getCurrentAssignments()
  const history = getAssignmentHistory()
  const existing = current.find(a => a.assetId === assetId)
  if (!existing) return null

  const now = new Date()
  const nowIso = now.toISOString()

  const nextCurrent = current.filter(a => a.assetId !== assetId)
  const newHistory: AssignmentHistoryEntry[] = [
    {
      id: `hu_${now.getTime()}`,
      action: 'UNASSIGN',
      assetId: existing.assetId, assetName: existing.assetName, assetNumber: existing.assetNumber, category: existing.category,
      employeeId: null, employeeName: null,
      previousEmployeeId: existing.employeeId, previousEmployeeName: existing.employeeName,
      effectiveDate: nowIso.slice(0, 10), reason, recordedByName, recordedAt: nowIso,
    },
    ...history,
  ]

  saveAll(nextCurrent, newHistory)
  return existing
}

// ── Weekly window helpers ──────────────────────────────────────────
// Weeks run Monday → Sunday.
export function getWeekRange(reference: Date = new Date()): { start: Date; end: Date; label: string } {
  const d = new Date(reference)
  const day = d.getDay() // 0=Sun..6=Sat
  const diffToMonday = (day + 6) % 7
  const start = new Date(d)
  start.setHours(0, 0, 0, 0)
  start.setDate(d.getDate() - diffToMonday)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  const label = `${start.toISOString().slice(0, 10)}_to_${end.toISOString().slice(0, 10)}`
  return { start, end, label }
}

export function getHistoryForWeek(reference: Date = new Date()): AssignmentHistoryEntry[] {
  const { start, end } = getWeekRange(reference)
  return getAssignmentHistory().filter(h => {
    const t = new Date(h.recordedAt).getTime()
    return t >= start.getTime() && t <= end.getTime()
  })
}

// ── Defect blocking ───────────────────────────────────────────────────────────────────
// Checks whether an employee has a currently assigned asset with OPEN
// CRITICAL or MAJOR defects. Used by rosterService.saveAllocation to warn
// or block allocation if the employee's plant is unfit for work.

export interface DefectBlock {
  blocked:       boolean
  assetId:       string
  assetName:     string
  assetNumber:   string
  openDefects:   Array<{ severity: string; description: string }>
}

/**
 * Returns defect block information for an employee.
 * Pass `equipment` — the full list from MOCK_EQUIPMENT or plantAssets —
 * so this function doesn’t import directly from mockDataExtended (keeps deps clean).
 */
export function getDefectBlockForEmployee(
  employeeId: string,
  equipment:  Array<{ id: string; assetNumber: string; name: string; defects?: Array<{ severity: string; status: string; description: string }> }>
): DefectBlock | null {
  const assignments = getCurrentAssignments()
  const assignment  = assignments.find(a => a.employeeId === employeeId)
  if (!assignment) return null

  const asset = equipment.find(e => e.id === assignment.assetId)
  if (!asset) return null

  const openBlockingDefects = (asset.defects ?? []).filter(
    d => d.status === 'OPEN' && (d.severity === 'CRITICAL' || d.severity === 'MAJOR')
  )
  if (openBlockingDefects.length === 0) return null

  return {
    blocked:     true,
    assetId:     asset.id,
    assetName:   asset.name,
    assetNumber: asset.assetNumber,
    openDefects: openBlockingDefects.map(d => ({ severity: d.severity, description: d.description })),
  }
}
